// Enhanced API Services - Centralized Export
import authService from './auth.service';
import catalogService from './catalog.service';
import bookingService from './booking.service';
import paymentService from './payment.service';
import tapPaymentService from './tap-payment.service';

export { default as authService } from './auth.service';
export { default as catalogService } from './catalog.service';
export { default as bookingService } from './booking.service';
export { default as paymentService } from './payment.service';
export { default as tapPaymentService } from './tap-payment.service';
export { default as settingsService } from './settings.service';

// Re-export types for convenience
export type {
  // Auth types
  User,
  AuthTokenData,
  OTPResponse,
  VerifyOTPResponse
} from './auth.service';

export type {
  // Catalog types
  Category,
  Trip,
  TimeSlot,
  Addon,
  CategoriesResponse,
  TripsResponse,
  TimeSlotsResponse,
  AddonsResponse
} from './catalog.service';

export type {
  // Booking types
  BookingHold,
  PassengerDetail,
  BookingOrderResponse,
  PriceCalculation,
  CouponResponse,
  CreateBookingOrderData
} from './booking.service';

export type {
  // Payment types
  PaymentMethod,
  PaymentMethodsResponse,
  PaymentInstructions,
  ProcessPaymentData,
  PaymentResult
} from './payment.service';

export type {
  // Tap Payment types
  TapPaymentRequest,
  TapPaymentResponse,
  TapVerificationResponse
} from './tap-payment.service';

export type {
  // Settings types
  PluginSettings,
  SettingsResponse
} from './settings.service';

// Re-export common types from config
export type { APIResponse, APIError } from '../../config/api.enhanced';

/**
 * Main API class that combines all services
 * This maintains backward compatibility with the old api.ts structure
 */
class BoatBookingAPI {
  // Auth methods delegation
  sendOtp = authService.sendOtp.bind(authService);
  verifyOtp = authService.verifyOtp.bind(authService);
  loginWithEmail = authService.loginWithEmail.bind(authService);
  getUserInfo = authService.getUserInfo.bind(authService);
  logout = authService.logout.bind(authService);
  isAuthenticated = authService.isAuthenticated.bind(authService);

  // Catalog methods delegation
  getCategories = catalogService.getCategories.bind(catalogService);
  getTrips = catalogService.getTrips.bind(catalogService);
  getTimeSlots = catalogService.getTimeSlots.bind(catalogService);
  getAddons = catalogService.getAddons.bind(catalogService);
  searchTrips = catalogService.searchTrips.bind(catalogService);
  getTripDetails = catalogService.getTripDetails.bind(catalogService);
  getCategoryDetails = catalogService.getCategoryDetails.bind(catalogService);
  getFeaturedTrips = catalogService.getFeaturedTrips.bind(catalogService);
  getTripsByPriceRange = catalogService.getTripsByPriceRange.bind(catalogService);

  // Booking methods delegation
  calculatePrice = bookingService.calculatePrice.bind(bookingService);
  createHold = bookingService.createBookingHold.bind(bookingService);
  updateBookingHold = bookingService.updateBookingHold.bind(bookingService);
  createOrder = bookingService.createBookingOrder.bind(bookingService);
  validateCoupon = bookingService.validateCoupon.bind(bookingService);
  applyCoupon = bookingService.applyCoupon.bind(bookingService);
  getUserBookings = bookingService.getUserBookings.bind(bookingService);
  cancelBooking = bookingService.cancelBooking.bind(bookingService);

  // Payment methods delegation
  getPaymentMethods = paymentService.getPaymentMethods.bind(paymentService);
  getPaymentMethodDetails = paymentService.getPaymentMethodDetails.bind(paymentService);
  getPaymentInstructions = paymentService.getPaymentInstructions.bind(paymentService);
  processPayment = paymentService.processPayment.bind(paymentService);

  // Tap Payment methods delegation
  createTapPayment = tapPaymentService.createPayment.bind(tapPaymentService);
  verifyTapPayment = tapPaymentService.verifyPayment.bind(tapPaymentService);
  pollTapPaymentStatus = tapPaymentService.pollPaymentStatus.bind(tapPaymentService);
  handleTapCallback = tapPaymentService.handleCallback.bind(tapPaymentService);
  openTapPaymentWindow = tapPaymentService.openPaymentWindow.bind(tapPaymentService);

  // Global settings
  setLanguage(lang: string): void {
    authService.setLanguage(lang);
    catalogService.setLanguage(lang);
    bookingService.setLanguage(lang);
    paymentService.setLanguage(lang);
  }

  // Backward compatibility methods
  calculatePricing = this.calculatePrice;
  createBookingHold = this.createHold;
  createBookingOrder = this.createOrder;
}

// Create singleton instance for backward compatibility
const apiService = new BoatBookingAPI();

export default apiService;
